if (!customElements.get("feature-strip")) {
  class FeatureStrip extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.navigationControllers = new Set();
      this.timers = new Set();
      this.resizeFrame = null;
      this.lastViewportWidth = 0;
      this.handleResize = this.handleWindowResize.bind(this);
    }

    connectedCallback() {
      this.abortController?.abort();
      this.cleanupStrips();
      this.abortController = new AbortController();
      this.lastViewportWidth = window.innerWidth;

      this.initializeStrips();
      this.recalculateAfterFontsLoad();

      window.addEventListener("resize", this.handleResize, {
        signal: this.abortController.signal,
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.cleanupStrips();

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
    }

    handleWindowResize() {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;

        const viewportWidth = window.innerWidth;
        if (viewportWidth === this.lastViewportWidth) return;

        this.lastViewportWidth = viewportWidth;
        this.initializeStrips();
      });
    }

    recalculateAfterFontsLoad() {
      if (document.fonts?.status !== "loading") return;

      document.fonts.ready.then(() => {
        if (!this.isConnected) return;

        this.initializeStrips();
      });
    }

    initializeStrips() {
      this.cleanupStrips();

      this.querySelectorAll("[data-strip-bar]").forEach((bar) => {
        this.setupStrip(bar);
      });
    }

    cleanupStrips() {
      this.querySelectorAll("[data-strip-bar]").forEach((bar) => {
        const track = bar.querySelector("[data-strip-track]");

        bar.classList.remove("is-auto-scrolling", "is-auto-fading", "is-auto-sliding");
        bar.style.removeProperty("--feature-strip-scroll-distance");
        bar.style.removeProperty("--feature-strip-scroll-duration");

        track?.style.removeProperty("transform");
        bar.querySelectorAll(".feature-strip__item").forEach((item) => {
          item.classList.remove("is-active");
          item.removeAttribute("aria-hidden");
        });
      });

      this.timers.forEach((timer) => window.clearInterval(timer));
      this.timers.clear();
      this.querySelectorAll("[data-strip-clone]").forEach((clone) => clone.remove());
      this.navigationControllers.forEach((controller) => controller.abort());
      this.navigationControllers.clear();
    }

    setupStrip(bar) {
      const viewport = bar.querySelector("[data-strip-viewport]");
      const track = bar.querySelector("[data-strip-track]");

      if (!viewport || !track) return;

      const items = Array.from(track.children).filter((item) => !item.hasAttribute("data-strip-clone"));

      if (items.length <= 1) return;

      const animation = bar.dataset.stripAnimation || "slide";
      const interval = Number(bar.dataset.stripInterval) || 5000;
      const autoRotate = bar.dataset.stripAutoRotate === "true";
      const scrollWhenMany = bar.dataset.stripScrollWhenMany === "true";
      const autoAdvance = autoRotate && !this.prefersReducedMotion();
      const isMobile = window.matchMedia("(max-width: 989px)").matches;

      if (isMobile) {
        if (animation === "loop") {
          if (autoAdvance) this.setupLoopingScroll(bar, viewport, track, items);
          return;
        }

        if (animation === "fade") {
          this.setupFadeScroll(bar, items, interval, autoAdvance);
          return;
        }

        this.setupSlidingScroll(bar, track, items, interval, autoAdvance);
        return;
      }

      const hasOverflow = this.measureContentWidth(track, items) > viewport.clientWidth + 1;
      const shouldAutoScroll = autoAdvance && (hasOverflow || (scrollWhenMany && items.length >= 5));

      if (!shouldAutoScroll) return;

      if (animation === "fade") {
        this.setupFadeScroll(bar, items, interval, autoAdvance);
        return;
      }

      this.setupLoopingScroll(bar, viewport, track, items);
    }

    // The track centers its items, so overflow spills to both sides and scrollWidth under-reports it.
    measureContentWidth(track, items) {
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap) || 0;
      const padding = (parseFloat(styles.paddingInlineStart) || 0) + (parseFloat(styles.paddingInlineEnd) || 0);
      const itemsWidth = items.reduce((total, item) => total + item.getBoundingClientRect().width, 0);

      return itemsWidth + gap * Math.max(items.length - 1, 0) + padding;
    }

    setupSlidingScroll(bar, track, items, interval, autoAdvance) {
      let activeIndex = 0;

      const render = () => {
        items.forEach((item, index) => {
          item.setAttribute("aria-hidden", index === activeIndex ? "false" : "true");
        });

        track.style.transform = `translateX(${activeIndex * -100}%)`;
      };

      bar.classList.add("is-auto-sliding");
      render();

      this.bindNavigation(bar, {
        count: items.length,
        interval,
        autoAdvance,
        getIndex: () => activeIndex,
        setIndex: (index) => {
          activeIndex = index;
          render();
        },
      });
    }

    setupFadeScroll(bar, items, interval, autoAdvance) {
      let activeIndex = 0;

      const render = () => {
        items.forEach((item, index) => {
          const isActive = index === activeIndex;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-hidden", isActive ? "false" : "true");
        });
      };

      bar.classList.add("is-auto-fading");
      render();

      this.bindNavigation(bar, {
        count: items.length,
        interval,
        autoAdvance,
        getIndex: () => activeIndex,
        setIndex: (index) => {
          activeIndex = index;
          render();
        },
      });
    }

    setupLoopingScroll(bar, viewport, track, items) {
      const appendCloneSet = () => {
        const fragment = document.createDocumentFragment();

        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute("aria-hidden", "true");
          clone.setAttribute("data-strip-clone", "");
          clone.querySelectorAll("a, button").forEach((element) => element.setAttribute("tabindex", "-1"));
          fragment.appendChild(clone);
        });

        track.appendChild(fragment);
      };

      appendCloneSet();

      const firstClone = track.querySelector("[data-strip-clone]");
      const scrollDistance = firstClone ? firstClone.offsetLeft - items[0].offsetLeft : track.scrollWidth / 2;

      if (scrollDistance <= 0) return;

      const cloneSetsNeeded = Math.ceil(viewport.clientWidth / scrollDistance) + 1;
      for (let index = 1; index < cloneSetsNeeded; index += 1) appendCloneSet();

      const scrollDuration = Math.max(scrollDistance / 40, 8);

      bar.style.setProperty("--feature-strip-scroll-distance", `${scrollDistance}px`);
      bar.style.setProperty("--feature-strip-scroll-duration", `${scrollDuration}s`);
      bar.classList.add("is-auto-scrolling");
    }

    bindNavigation(bar, { count, interval, autoAdvance, getIndex, setIndex }) {
      let timer = null;

      const goTo = (index) => setIndex(((index % count) + count) % count);

      const startTimer = () => {
        if (!autoAdvance) return;

        timer = window.setInterval(() => {
          if (this.shouldPause(bar)) return;

          goTo(getIndex() + 1);
        }, interval);

        this.timers.add(timer);
      };

      // Restart after a manual step so the next auto-advance is not cut short.
      const restartTimer = () => {
        if (timer) {
          window.clearInterval(timer);
          this.timers.delete(timer);
          timer = null;
        }

        startTimer();
      };

      const navigationController = new AbortController();
      this.navigationControllers.add(navigationController);

      const bindArrow = (selector, step) => {
        bar.querySelectorAll(selector).forEach((button) => {
          button.addEventListener(
            "click",
            () => {
              goTo(getIndex() + step);
              restartTimer();
            },
            { signal: navigationController.signal },
          );
        });
      };

      bindArrow("[data-strip-prev]", -1);
      bindArrow("[data-strip-next]", 1);

      startTimer();
    }

    prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    shouldPause(bar) {
      const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      return (canHover && bar.matches(":hover")) || bar.contains(document.activeElement);
    }
  }

  customElements.define("feature-strip", FeatureStrip);
}
